<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useScreenSize } from "../../../utils/useScreenSize";
import FormHeader from "./FormHeader.vue";
import { Tabs } from "../../table/table";
import { Permission } from "../../../types/auth";
//import MessageBox from "../../../services/MessageBox";
import { tokenManager } from "../../../api/utils/tokenManager";
import { getPermissionList } from "../../../utils/CommonFunctions";
import { driver } from "driver.js";
import NoPermissions from "../common/NoPermissions.vue";
import FormFooter from "./FormFooter.vue";

const { isMobile } = useScreenSize();

interface Props {
  width?: string;
  header?: boolean;
  fullscreen?: boolean;
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint?: string;
  icon?: string;
  listCreateName?: string;
  tabList?: Tabs[];
  formData?: any;
  perm?: Permission[];
  modal?: boolean;
  showEdit?: boolean;
  showTour?: boolean;
  saveCloseCaption?: string;
  saveCloseIcon?: string;
  ignorePermission?: boolean;
  showSave?: boolean;
  showSaveClose?: boolean;
  showSaveNew?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  fullscreen: false,
  modal: true,
  ignorePermission: false,
  showEdit: false,
  showTour: false,
  showSave: true,
  showSaveClose: true,
  showSaveNew: true,
  width: "50rem",
});

const modalRef = ref<HTMLElement | null>(null);
const modalRoot = ref<HTMLElement | null>(null);

// Create modal root element
onMounted(() => {
  modalRoot.value = document.createElement("div");
  modalRoot.value.id = "modal-root";
  document.body.appendChild(modalRoot.value);
  document.body.style.overflow = "hidden";
  checkPermission();
});

// Cleanup on unmount
onBeforeUnmount(() => {
  document.body.style.overflow = "";
  if (modalRoot.value && document.body.contains(modalRoot.value)) {
    document.body.removeChild(modalRoot.value);
  }
});

const emit = defineEmits<{
  (e: "save", close: boolean, newRecord: boolean): void;
  (e: "close"): void;
  (e: "handleEdit"): void;
  (e: "handleCancel"): void;
}>();

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  emit("save", close, newRecord);
};
const isReadOnly = props.mode === "view";
const noPermission = ref(true);
const onTour = ref(false);

//const route = useRoute();
//const path = computed(() =>route.path)
const permissions = ref<Permission[]>();

// Initialize driver.js tour
const driverObj = driver({
  showProgress: true,
  allowClose: true,
  onCloseClick: () => {
    onTour.value = false;
    driverObj.destroy();
  },
  onDestroyStarted: () => {
    onTour.value = false;
    driverObj.destroy();
  },
  steps: [
    {
      element: "#form-header",
      popover: {
        title: "Form Header",
        description:
          "Title form on the left and the available actions on the right side",
        side: "bottom",
      },
    },
    {
      element: "#form-actions",
      popover: {
        title: "Form Actions",
        description: "Available actions for the screen.",
        side: "top",
      },
    },
    {
      element: "#form-update-exchange-rate",
      popover: {
        title: "Update Exchange Rates",
        description: "Click the Update Exchange Rates.",
        side: "left",
      },
    },
    {
      element: "#form-save-new",
      popover: {
        title: "Save & New Record",
        description:
          "Save the current record and prepare the screen for a next record",
        side: "right",
      },
    },
    {
      element: "#form-save",
      popover: {
        title: "Save",
        description: "Save the Current changes",
        side: "left",
      },
    },
    {
      element: "#form-save-close",
      popover: {
        title: "Save & Close",
        description: "Save the Current Record and Close the Screen.",
        side: "left",
      },
    },
    {
      element: "#form-fields",
      popover: {
        title: "Form Fields",
        description: "All the fields that can be updated for the record.",
        side: "left",
      },
    },
    {
      element: "#form-extra-info",
      popover: {
        title: "Extra Info",
        description:
          "This panel will show extra information for the current record, the Panel wont be visible when a new record is being created.",
        side: "left",
      },
    },
    {
      element: "#form-extra-info-tabs",
      popover: {
        title: "Tabs",
        description:
          "Available Tabs as extra info, you can toggle between the tabs to view details",
        side: "left",
      },
    },
    {
      element: "#extra-info-exchange-rates",
      popover: {
        title: "Exchange Rates",
        description: "Most recent Exchange Rates.",
        side: "left",
      },
    },
    {
      element: "#extra-info-activity-log",
      popover: {
        title: "Activity Log",
        description:
          "User Activity Log, this is visible only if you have the access rights.",
        side: "left",
      },
    },
    {
      element: "#form-footer-actions",
      popover: {
        title: "Form Actions",
        description: "Available actions to perform on the record",
        side: "top",
      },
    },
  ],
});

const startTour = () => {
  onTour.value = true;
  driverObj.drive();
};

const initTour = () => {
  onTour.value = false;
  const hasSeenFormTour = localStorage.getItem(
    "quickTour" + props.title + "Form"
  );
  if (!hasSeenFormTour) {
    onTour.value = true;
    driverObj.drive();
    localStorage.setItem("quickTour" + props.title + "Form", "true");
  }
};

const checkPermission = () => {
  if (props.perm) {
    permissions.value = props.perm;
  } else {
    let menu = tokenManager
      .getPermissions()
      .find((m: any) => m.name === props.title);
    if (!menu && props.endPoint) {
      // Try to find by endpoint if not found by name
      menu = tokenManager
        .getPermissions()
        .find((m: any) => m.route_path === props.endPoint?.replace('/clients/', ''));
    }
    permissions.value = getPermissionList(menu?.route_path || "");
  }
  if (
    !props.ignorePermission &&
    (!permissions.value || permissions.value.length === 0)
  ) {
    /*MessageBox.show(
      "You do not have permission to use this feature (" + props.title + ")",
      "warning"
    );*/
    noPermission.value = true;
  } else {
    noPermission.value = false;
    initTour();
  }
};

const modalStyle = computed(() => {
  if (props.fullscreen || isMobile.value == true) {
    return {
      width: "100%",
      height: "100%",
      maxWidth: "100%",
      maxHeight: "100%",
      margin: "0",
      borderRadius: "0",
    };
  }
  return {
    width: props.width,
    maxWidth: "100%",
    maxHeight: "calc(100vh - 40px)", // 20px margin top and bottom
    margin: "20px auto",
  };
});


watch(
  () => props.perm,
  (newMode) => {
    if (newMode) {
      //permissions.value = newMode;
      checkPermission();
    }
  }
);
</script>

<template>
  <Teleport to="body" :disabled="!modalRoot">
    <div
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-auto"
      :class="{ 'p-0': fullscreen }"
    >
      <div
        ref="modalRef"
        class="bg-white dark:bg-gray-800 shadow-xl flex flex-col"
        :class="[fullscreen ? '' : 'rounded-lg']"
        :style="modalStyle"
      >
        <!-- Fixed Header -->
        <div
          class="bg-white dark:bg-gray-800 p-3 border-b border-gray-200 dark:border-gray-700"
          :class="[fullscreen ? '' : 'rounded-t-lg']"
        >
          <div>
            <FormHeader
              :mode="mode"
              :listCreateName="listCreateName"
              :showEdit="showEdit"
              :endPoint="endPoint"
              :showTour="showTour"
              :title="title"
              :noPermission="noPermission"
              :saveCloseCaption="saveCloseCaption"
              :saveCloseIcon="saveCloseIcon"
              :showSave="showSave"
              :showSaveClose="showSaveClose"
              :showSaveNew="showSaveNew"
              :onTour="onTour"
              :modal="modal"
              class="sticky top-0 z-10 bg-white dark:bg-gray-800"
              @close="$emit('close')"
              @handleEdit="$emit('handleEdit')"
              @handleCancel="$emit('handleCancel')"
              @startTour="startTour"
              @handleSubmit="handleSubmit"
            >
              <template #form-header-buttons>
              <slot name="header"></slot>
              </template>
            </FormHeader>

            <!--<div class="overflow-auto max-h-[calc(100vh-150px)]"> Modal FOrm Scrolling changed-->
            <div>
              <form
              @submit.prevent="null"
              class="p-4 md:p-6 space-y-6"
              v-if="!noPermission"
              >
              <slot name="form"></slot>
              </form>
            </div>

            <FormFooter
              v-if="!noPermission"
              :on-tour="onTour"
              :mode="mode"
              :modal="modal"
              :title="title"
              :showSave="showSave"
              :showSaveClose="showSaveClose"
              :showSaveNew="showSaveNew"
              :listCreateName="listCreateName"
              :endPoint="endPoint"
              :noPermission="noPermission"
              :isReadOnly="isReadOnly"
              @handleSubmit="handleSubmit"
              @close="$emit('close')"
            >
            </FormFooter>

            <NoPermissions
              v-if="noPermission"
              @close="$emit('close')"
              :modal="true"
            />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
